import { useEffect, useMemo, useState } from "react";
import { FiCalendar, FiExternalLink, FiPower, FiRefreshCw } from "react-icons/fi";
import { Sidebar } from "../components/Note";
import api from "../api";
import "../styles/tailwind.output.css";

export default function Soporte() {
  const [dark, setDark] = useState(() => {
    if (typeof document === "undefined") return true;
    return document.documentElement.classList.contains("dark");
  });
  const [loading, setLoading] = useState(true);
  const [userConnected, setUserConnected] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");
  const [baseEmbedUrl, setBaseEmbedUrl] = useState("");
  const [loadingEmbed, setLoadingEmbed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [authUser, setAuthUser] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const sync = () => setDark(root.classList.contains("dark"));
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    const onStorage = (event) => {
      if (event.key === "theme") sync();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const refreshStatus = async () => {
    try {
      const { data } = await api.get("google/status/");
      const user = data?.user || {};
      setUserConnected(!!user.connected);
    } catch {
      setUserConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const buildEmbed = async () => {
    setLoadingEmbed(true);
    setError("");
    try {
      const { data } = await api.get("google/calendars/", { params: { target: "user" } });
      const items = Array.isArray(data?.items) ? data.items : [];
      const primaryItem = items.find((it) => it.primary) || items[0];

      if (!primaryItem?.id) {
        setEmbedUrl("");
        setError("No se encontró un calendario disponible en tu cuenta.");
        return;
      }

      setAuthUser(primaryItem.id);
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      const params = new URLSearchParams({
        ctz: tz,
        showTitle: "0",
        mode: "MONTH",
        showDate: "1",
        showNav: "1",
        showPrint: "0",
        showTabs: "1",
        showCalendars: "0",
        showTz: "0",
        wkst: "1",
        hl: "es",
        bgcolor: "%23000000",
      });
      params.append("src", primaryItem.id);
      params.append("authuser", primaryItem.id);

      const base = `https://calendar.google.com/calendar/embed?${params.toString()}`;
      setBaseEmbedUrl(base);
      setEmbedUrl(`${base}&_=${Date.now()}`);
    } catch (err) {
      setEmbedUrl("");
      setError(String(err?.response?.data?.detail || "No se pudo cargar el calendario"));
    } finally {
      setLoadingEmbed(false);
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  useEffect(() => {
    if (userConnected) {
      buildEmbed();
    } else {
      setEmbedUrl("");
      setBaseEmbedUrl("");
      setAuthUser("");
    }
  }, [userConnected]);

  const onConnect = async () => {
    try {
      const { data } = await api.get("google/auth-url/", { params: { target: "user" } });
      const url = data?.auth_url;
      if (url) {
        window.location.href = url;
      } else {
        setError("No se pudo obtener URL de autorización");
      }
    } catch (e) {
      setError(String(e?.response?.data?.detail || e?.message || "Error al conectar Google"));
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm("¿Deseas desconectar Google Calendar?")) return;
    try {
      await api.post("google/disconnect/", { target: "user" });
      await refreshStatus();
    } catch (e) {
      setError(String(e?.response?.data?.detail || "Error al desconectar"));
    }
  };

  const refreshEmbed = async () => {
    if (!baseEmbedUrl) {
      await buildEmbed();
      return;
    }
    setRefreshing(true);
    setEmbedUrl(`${baseEmbedUrl}&_=${Date.now()}`);
    window.setTimeout(() => setRefreshing(false), 500);
  };

  const googleCalendarHome = useMemo(() => {
    if (authUser) return `https://calendar.google.com/calendar/r?authuser=${encodeURIComponent(authUser)}`;
    return "https://calendar.google.com/calendar/r";
  }, [authUser]);

  return (
    <div className={`ws-page ws-page-soporte flex min-h-screen ${dark ? "bg-[#050d1c] text-slate-100" : "bg-[#eef3fb] text-[#12243d]"}`}>
      <Sidebar />

      <main className="flex-1 px-4 py-6 md:px-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <header className={`ws-header-surface rounded-2xl border px-6 py-5 ${dark ? "border-[#21324d] bg-[#10192a]" : "border-[#d4deec] bg-white"}`}>
            <h1 className={`text-3xl font-bold tracking-tight ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Google Calendar</h1>
            <p className={`mt-1 text-sm ${dark ? "text-[#8ba3c3]" : "text-[#4d688a]"}`}>Sincronización de eventos</p>
          </header>

          {loading ? (
            <section className={`ws-panel rounded-2xl border px-6 py-14 text-center ${dark ? "border-[#25344f] bg-[#101a2c] text-[#8ea5c4]" : "border-[#d4deec] bg-white text-[#4d688a]"}`}>
              Cargando estado de Google Calendar...
            </section>
          ) : userConnected ? (
            <section className={`ws-panel rounded-2xl border p-5 md:p-6 ${dark ? "border-[#25344f] bg-[#101a2c]" : "border-[#d4deec] bg-white"}`}>
              <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-[#25344f] pb-4">
                <a
                  href={googleCalendarHome}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/45 bg-emerald-500/12 px-4 py-2 text-sm font-semibold text-emerald-300 transition hover:border-emerald-400 hover:bg-emerald-500/18"
                >
                  <FiExternalLink size={14} />
                  Abrir Google Calendar
                </a>

                <button
                  type="button"
                  onClick={onDisconnect}
                  className="inline-flex items-center gap-2 rounded-lg border border-rose-500/45 bg-rose-500/12 px-4 py-2 text-sm font-semibold text-rose-300 transition hover:border-rose-400 hover:bg-rose-500/18"
                >
                  <FiPower size={14} />
                  Desconectar
                </button>

                <button
                  type="button"
                  onClick={refreshEmbed}
                  disabled={loadingEmbed || refreshing}
                  className="ml-auto inline-flex items-center gap-2 rounded-lg border border-[#3a516f] bg-[#17253d] px-4 py-2 text-sm font-semibold text-[#c5d7f0] transition hover:border-[#4b6891] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiRefreshCw className={loadingEmbed || refreshing ? "animate-spin" : ""} size={14} />
                  Actualizar
                </button>
              </div>

              {error && (
                <div className="mb-4 rounded-xl border border-rose-500/35 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {error}
                </div>
              )}

              {embedUrl ? (
                <iframe
                  title="Google Calendar"
                  src={embedUrl}
                  className={`h-[680px] w-full rounded-xl border ${dark ? "border-[#25344f] bg-[#0c1526]" : "border-[#d4deec] bg-[#f8fbff]"}`}
                />
              ) : (
                <div className={`flex min-h-[320px] items-center justify-center rounded-xl border border-dashed ${dark ? "border-[#2f4566] bg-[#0c1526] text-[#8ea5c4]" : "border-[#d4deec] bg-[#f8fbff] text-[#4d688a]"}`}>
                  No se pudo cargar la vista de calendario.
                </div>
              )}
            </section>
          ) : (
            <section className={`ws-panel rounded-2xl border px-6 py-16 ${dark ? "border-[#25344f] bg-[#0a1424]" : "border-[#d4deec] bg-white"}`}>
              <div className="mx-auto flex max-w-3xl flex-col items-center justify-center text-center">
                <span className={`mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full ${dark ? "bg-[#1a2a44] text-[#b9cceb]" : "bg-[#e7eef9] text-[#2b4b71]"}`}>
                  <FiCalendar size={30} />
                </span>
                <h2 className={`text-3xl font-semibold ${dark ? "text-[#e8eef9]" : "text-[#14263f]"}`}>Google Calendar</h2>
                <p className={`mt-3 text-xl ${dark ? "text-[#8ea5c4]" : "text-[#4d688a]"}`}>
                  Conecta tu Google Calendar para enviar recordatorios automáticos por WhatsApp
                </p>

                <button
                  type="button"
                  onClick={onConnect}
                  className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#23d767] px-6 py-3 text-lg font-semibold text-[#04131f] transition hover:bg-[#2de073]"
                >
                  Conectar Google Calendar
                </button>

                {error && <p className="mt-4 text-sm text-rose-300">{error}</p>}
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}